<template>
  <create-form
    v-if="isCreateModalOpened"
    :is-create-modal-opened="isCreateModalOpened"
    :refresh-data="getICDData"
    :icdData="Icds"
    @toggle-modal="isCreateModalOpened = false"
    @new-mac-created="refreshNewMacCreated"
  />
  <assign-modal-form
    v-if="isAssignModalOpened"
    :is-assign-icd-modal-opened="isAssignModalOpened"
    :sensor-mac="selectedICDData.mac"
    @toggle-modal="isAssignModalOpened = false"
    @refresh-icd-report="getICDData"
  />
  <main-section>
    <loader v-if="isLoading" />
    <div v-else class="h-full">
      <table-action-header
        :icon="['fas', 'add']"
        :permission-id="permissions.CreateAssignment"
        :title="$t('ICDManagement.Title')"
        :button-text="$t('ICDManagement.Assign')"
        @execute-method="toggleCreateModal"
      />
      <custom-table
        :is-action-list-not-displayed="true"
        :is-data-source-loading="isDataSourceLoading"
        :show-per-page="16"
        :table-data="Icds"
        :table-headers="tableHeadersICD"
        :allow-updating-prop=true
        table-height="93%"
        @force-refresh="getICDData"
        @selected-rows="getSelectedRows"
        @unassign-icd-tag="onUnassign"
        @edit-icd-tag-name="editIcdTagName"
        @assign-icd-tag="toggleAssignIcdModal"
      />
    </div>
  </main-section>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import MainSection from "@/core/Components/MainSection.vue";
import {
  AssignIcd,
  ICD,
  ICDCustomer,
  ICDMac,
} from "@/modules/Parkom/Submodules/ICDManagement/Services/ICDManagementTypes";
import { useToast } from "vue-toastification";
import { ErrorMessageFormatter, isString } from "@/core/Services/GlobalFunctions";
import CustomTable from "@/core/Components/CustomTable.vue";
import TableActionHeader from "@/core/Components/TableActionHeader.vue";
import Loader from "@/core/Components/Loader.vue";
import { useChangeCompanyStore } from "@/core/Store/changeCompanyStore";
import { usePermissionStore } from "@/core/Store/permissionStore";
import { Permissions } from "@/core/Services/Enums";
import CreateForm from "../Components/CreateForm.vue";
import IcdService from "../Services/ICDManagementService";
import AssignModalForm from "@/modules/Parkom/Submodules/ICDManagement/Components/AdditionalModals/AssignICD.vue";


export default defineComponent({
  components: {
    Loader,
    TableActionHeader,
    CustomTable,
    MainSection,
    CreateForm,
    AssignModalForm
  },
  data() {
    return {
      permissionStore: usePermissionStore(),
      selectedICDData: {} as ICD,
      icdService: new IcdService(),
      toast: useToast(),
      changeCompanyStore: useChangeCompanyStore(),
      isSubmitLoading: false,
      isLoading: false,
      isDataSourceLoading: false,
      isCreateModalOpened: false,
      isAssignModalOpened: false,
      permissions: Permissions,
      tableHeadersICD: {
        mac: { header: "ICDManagement.MAC", visible: true},
        "customer.name": { header: "ICDManagement.Tag", visible: true , allowUpdateColumn: true},
        unassign: { header: "Action.Unassign", visible: true },
      },
      Icds: [] as ICD[],
      Customers: [] as ICDCustomer[],
      sensor: {
        mac: "",
      } as ICDMac,
      customer: {
        id: null,
        name: "",
      } as ICDCustomer,
      icd: {
        mac: "",
        customerId: null,
      } as AssignIcd,
    };
  },

  async created() {
    this.isLoading = true;
    try {
      await this.getICDData();
    } catch (error: any) {
      if (!isString(error.response.data)) {
        this.toast.error("An unexpected error occurred.");
        return;
      }
      this.toast.error(error.response.data);
    } finally {
      this.isLoading = false;
    }
  },
  watch: {
    async "changeCompanyStore.companyId"() {
      await this.getICDData();
    },
  },

  methods: {
    async getICDData() {
      this.isDataSourceLoading = true;
      try {
        this.Icds = await this.icdService.getIcds({
          pageNumber: 1,
          pageSize: 100000,
        });        
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isDataSourceLoading = false;
      }
    },

    async handleUnassign() {
      this.isSubmitLoading = true;
      try {
        this.icd = {
          mac: this.selectedICDData.mac,
          customerId: this.selectedICDData.customerId,
        };

        await this.icdService.unassignIcd(this.icd);
        this.toast.success("ICD successfully unassigned!");
        await this.getICDData();
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isSubmitLoading = false;
      }
    },

    getSelectedRows(values: ICD[]) {
      if (values.length) {
        this.selectedICDData = values[0];
      }
    },

    async onUnassign(row: ICD) {
      this.getSelectedRows([row]);
      this.$nextTick(async () => {
        await this.handleUnassign();
      });
    },

    async refreshNewMacCreated() {
      await this.getICDData();
    },

    toggleCreateModal() {
      this.isCreateModalOpened = !this.isCreateModalOpened;
    },

    async editIcdTagName(row: ICD){
        if(row.customer.id == undefined)
        { 
          this.toast.warning("Empty tag name cannot be updated!");
          this.getICDData();
          return;
        }
        this.isSubmitLoading = true;
      try {
        var icdCustomer = {
          id: row.customer.id,
          name: row.customer.name
        };
        if(icdCustomer.id == 0) return;
        await this.icdService.updateICdCustomerTagName(icdCustomer);
        this.toast.success("Tag name successfully updated!");
      } catch (error: any) {
        if (!isString(error.response.data)) {
          this.toast.error(ErrorMessageFormatter(error));
          return;
        }
        this.toast.error(error.response.data);
      } finally {
        this.isSubmitLoading = false;
      }
    },

    async toggleAssignIcdModal(){
        this.isAssignModalOpened = !this.isAssignModalOpened;
    }
  },
});
</script>

<style scoped></style>
